/**
 * Network utilities for handling online/offline status
 */
import { useState, useEffect } from 'react';

/**
 * Custom hook to track online/offline status
 * @returns {boolean} - True if online, false if offline
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Event handlers for online/offline status changes
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Clean up event listeners on unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

/**
 * Function to register the service worker
 * @returns {Promise<void>}
 */
export const registerServiceWorker = async () => {
  // Allow browser cache to work in development mode
  console.log('🔄 Browser cache enabled in development mode for testing');
  
  // In a production environment, we would register the service worker here
  if ('serviceWorker' in navigator) {
    try {
      // Skip unregistering service workers
      const registrations = await navigator.serviceWorker.getRegistrations();
      console.log(`ℹ️ Found ${registrations.length} service worker registrations (keeping them active)`);
      
      // Skip clearing caches to allow browser cache to work
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        console.log(`ℹ️ Found ${cacheNames.length} browser caches (keeping them intact for testing)`);
      }
      
      console.log('✅ Browser caching enabled for testing');
    } catch (error) {
      console.error('Error checking service workers/caches:', error);
    }
  }
  return;
  
  // Unreachable code removed
};

/**
 * Check if the app is installed (in standalone mode)
 * @returns {boolean} - True if the app is installed
 */
export const isAppInstalled = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone || 
         document.referrer.includes('android-app://');
};
